import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable, Injector, untracked } from '@angular/core';
import { concatMap, filter, map, switchMap } from 'rxjs/operators';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { combineLatest, Observable, of } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import {
  IFpCorrelationManagerState,
  initialFpCorrelationManagerState,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/services/fp-correlation-manager/fp-correlation-manager.state';
import {
  FpCorrelationManagerActionChangeTab,
  FpCorrelationManagerActionGetSelectedDataUpdated,
  FpCorrelationManagerActionGlobalDataLoadingStateChanged,
  FpCorrelationManagerActionInit,
  FpCorrelationManagerActions,
  FpCorrelationManagerActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/services/fp-correlation-manager/fp-correlation-manager.actions';
import { Store } from '@ngrx/store';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { selectMainDate } from '@app/main/main-screen/store/main-selection/main-date.selectors';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { FP_CORRELATION_TAB_CONNECT_SERVICES } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/providers/fp-correlation-tab-connect-services.token';
import { FpCorrelationTabs } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/enums/fp-correlation-tabs.enum';
import { IFpCorrelationData } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/interfaces/fp-correlation-data';
import {
  selectGlobalMainDataIsLoaded,
  selectGlobalMainDataIsLoading,
} from '@app/main/main-screen/store/main-data/main-data.selectors';
import { selectAllCorrelations } from '@app/main/main-screen/store/data/correlations/correlations.selectors';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { ITranslationSubscription } from '@app/core/interfaces/localization';
import {
  getSerialNoPairKey,
  getSerialNoPairMapToCorrelations,
} from '@app/main/leak-detection/utils/correlation-serial-no-pair';
import { selectLoggerDeploymentEntities } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { ILoggerDeployment } from '@app/api/interfaces/logger-deployment-data.interface';
import { Dictionary } from '@ngrx/entity';
import { LdDataAggregationService } from '@app/main/leak-detection/services/leak-detection-data-aggregation/leak-detection-data-aggregation.service';

@Injectable()
export class FpCorrelationManagerStore
  extends ComponentStore<IFpCorrelationManagerState>
  implements IGcComponentStore<FpCorrelationManagerActions>
{
  private injector = inject(Injector);
  private store: Store = inject(Store);
  public readonly data = inject<IFpCorrelationData>(FLOATING_POPUP_DATA);
  private readonly tabConnectServices = inject(FP_CORRELATION_TAB_CONNECT_SERVICES);
  public readonly floatingPopupTabs = inject(FloatingPopupTabsService);
  private ldDataAggregationService = inject(LdDataAggregationService);
  private floatingPopupService = inject(FloatingPopupService);
  private formattingData = inject(FormattingDataService);

  private loggers$ = this.ldDataAggregationService.ldLoggers$;

  private mainDate$ = this.store.select(selectMainDate);
  private mainDate = toSignal<string | undefined>(this.mainDate$);

  private allCorrelations$: Observable<ICorrelation[]> = this.store.select(selectAllCorrelations);
  private allCorrelations = toSignal(this.allCorrelations$, { initialValue: [] as ICorrelation[] });

  private loggerDeploymentEntities$ = this.store.select(selectLoggerDeploymentEntities);
  private loggerDeploymentEntities = toSignal(this.loggerDeploymentEntities$, {
    initialValue: {} as Dictionary<ILoggerDeployment>,
  });

  private globalMainDataIsLoading$ = this.store.select(selectGlobalMainDataIsLoading);
  private globalMainDataIsLoaded$ = this.store.select(selectGlobalMainDataIsLoaded);

  private newestSelectedData$ = this.globalMainDataIsLoaded$.pipe(
    takeUntilDestroyed(),
    switchMap(isGlobalDataLoaded => {
      return combineLatest([this.allCorrelations$, this.loggers$]).pipe(filter(() => isGlobalDataLoaded));
    }),
    map(([correlations, loggers]) => this.findProperFpCorrelationData(correlations, loggers))
  );

  constructor() {
    super(initialFpCorrelationManagerState);

    this.popupId$
      .pipe(
        filter((popupId): popupId is string => !!popupId),
        takeUntilDestroyed(),
        switchMap(popupId =>
          untracked(() => toObservable(this.floatingPopupTabs.getActive(popupId), { injector: this.injector }))
        ),
        filter((activeTab): activeTab is FpCorrelationTabs => !!activeTab)
      )
      .subscribe(activeTab => {
        const activeTabConnectService = this.tabConnectServices.find(s => s.isActive(activeTab)) || null;

        this.dispatch({
          type: FpCorrelationManagerActionType.ChangeTab,
          activeTabConnectService,
        });
      });

    this.newestSelectedData$.pipe(takeUntilDestroyed()).subscribe(selectedData => {
      this.dispatch({
        type: FpCorrelationManagerActionType.SelectedDataUpdated,
        selectedData,
      });
    });

    this.globalMainDataIsLoading$.pipe(takeUntilDestroyed()).subscribe(isLoading => {
      this.dispatch({
        type: FpCorrelationManagerActionType.GlobalDataLoadingStateChanged,
        loading: isLoading,
      });
    });
  }

  public readonly popupId$ = this.select(s => s.popupId, { equal });
  public readonly selectedData$ = this.select(s => s.selectedData, { equal });
  public readonly initialized$ = this.select(s => s.initialized, { equal });

  public readonly activeTabConnectService$ = this.select(s => s.activeTabConnectService, { equal });

  public activeTabLoading$ = this.activeTabConnectService$.pipe(
    filter(tabConnectService => !!tabConnectService),
    switchMap(tabConnectService => tabConnectService?.localLoading())
  );

  public activeTabIsChartFullscreen$ = this.activeTabConnectService$.pipe(
    filter(tabConnectService => !!tabConnectService),
    switchMap(tabConnectService =>
      tabConnectService?.isChartFullscreen ? tabConnectService?.isChartFullscreen() : of(false)
    )
  );

  public readonly loading$ = this.select(s => s.loading, { equal });

  public dispatch = this.effect((action$: Observable<FpCorrelationManagerActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpCorrelationManagerActionType.Init: {
            this.reducerInit(action);
            this.effectInit(action);
            break;
          }
          case FpCorrelationManagerActionType.SelectedDataUpdated: {
            this.reducerSelectedDataUpdated(action);
            this.effectSelectedDataUpdated(action);
            break;
          }
          case FpCorrelationManagerActionType.ChangeTab: {
            this.reducerChangeActiveTabConnectService(action);
            this.effectChangeActiveTabConnectService(action);
            break;
          }
          case FpCorrelationManagerActionType.GlobalDataLoadingStateChanged: {
            this.reducerUpdateLoading(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerInit(action: FpCorrelationManagerActionInit) {
    const state = this.get();

    if (state.initialized) {
      return;
    }

    this.patchState({
      popupId: action.popupId,
      selectedData: action.selectedData,
      initialized: true,
    });
  }

  private reducerSelectedDataUpdated(action: FpCorrelationManagerActionGetSelectedDataUpdated) {
    this.patchState({
      selectedData: action.selectedData,
    });
  }

  private reducerChangeActiveTabConnectService(action: FpCorrelationManagerActionChangeTab) {
    this.patchState({
      activeTabConnectService: action.activeTabConnectService,
    });
  }

  private reducerUpdateLoading(action: FpCorrelationManagerActionGlobalDataLoadingStateChanged) {
    this.patchState({
      loading: action.loading,
    });
  }

  private effectInit(action: FpCorrelationManagerActionInit) {
    this.updateTitleInfo(action.selectedData);
  }

  private effectSelectedDataUpdated(action: FpCorrelationManagerActionGetSelectedDataUpdated) {
    const state = this.get();

    const mainDate = this.mainDate();
    const selectedData = action.selectedData;
    const activeTabConnectService = state.activeTabConnectService;

    if (mainDate && selectedData && activeTabConnectService) {
      activeTabConnectService.updateData(mainDate, selectedData);
    }

    this.updateTitleInfo(action.selectedData);
  }

  private effectChangeActiveTabConnectService(action: FpCorrelationManagerActionChangeTab) {
    const state = this.get();

    const mainDate = this.mainDate();
    const selectedData = state.selectedData;

    if (mainDate && selectedData && action.activeTabConnectService) {
      action.activeTabConnectService.initData(mainDate, selectedData);
    }
  }

  private findProperFpCorrelationData(correlations: ICorrelation[], loggers: ILdLogger[]): IFpCorrelationData | null {
    const idMap = new Map(correlations.map(c => [c.id, c]));

    let currentCorrelation = idMap.get(this.data.correlation.id);
    const loggersBySNo = new Map(loggers.map(ld => [ld.deployment.serialNo, ld]));
    const newLoggerDeployment1 = loggersBySNo.get(this.data.loggerDeployment1.serialNo)?.deployment;
    const newLoggerDeployment2 = loggersBySNo.get(this.data.loggerDeployment2.serialNo)?.deployment;

    if (!newLoggerDeployment1 || !newLoggerDeployment2) {
      return null;
    }

    if (!currentCorrelation) {
      currentCorrelation = correlations.find(
        cor =>
          (cor.logger1DeploymentId === newLoggerDeployment1.deploymentId &&
            cor.logger2DeploymentId === newLoggerDeployment2.deploymentId) ||
          (cor.logger1DeploymentId === newLoggerDeployment2.deploymentId &&
            cor.logger2DeploymentId === newLoggerDeployment1.deploymentId)
      );
    }

    if (!currentCorrelation) {
      return null;
    }

    return {
      correlation: { ...currentCorrelation },
      loggerDeployment1: { ...newLoggerDeployment1 },
      loggerDeployment2: { ...newLoggerDeployment2 },
    };
  }

  private updateTitleInfo(selectedData: IFpCorrelationData | null) {
    const popupId = this.get().popupId;

    if (!popupId) {
      return;
    }

    this.floatingPopupService.updateTitleInfo(popupId, this.getTitleInfo(selectedData));
  }

  private getTitleInfo(selectedData: IFpCorrelationData | null): ITranslationSubscription | undefined {
    if (!selectedData) {
      return undefined;
    }

    const serialNoPairMapToCorrelations = getSerialNoPairMapToCorrelations(
      this.allCorrelations(),
      this.loggerDeploymentEntities()
    );

    const serialNoPairKey = getSerialNoPairKey(
      selectedData.loggerDeployment1.serialNo,
      selectedData.loggerDeployment2.serialNo
    );

    const isExistAnotherRelatedCorrelation = (serialNoPairMapToCorrelations[serialNoPairKey]?.length ?? 0) > 1;

    return isExistAnotherRelatedCorrelation
      ? of(this.formattingData.formatTime(selectedData.correlation.soundSignalStartAt, DayJSDateFormat.LT))
      : undefined;
  }
}
